/**
 * NewBadge — small animated pill that says "NEU" / "NUEVO" for 48 hours
 * after a teacher publishes content.
 *
 * Designed to be drop-in: just pass `createdAt` (ISO string) and optionally
 * `authorRole` to scope it to teacher uploads only.
 *
 * Animation: a soft pulsing ring + a subtle bounce-in on mount. CSS-only,
 * lives next to the parent header / card title.
 */
import React, { useMemo } from 'react';
import { Sparkle } from '@phosphor-icons/react';
import { useLang } from '../contexts/LangContext';

const WINDOW_MS = 48 * 60 * 60 * 1000;

export default function NewBadge({ createdAt, authorRole, className = '' }) {
  const { lang } = useLang();

  const isFresh = useMemo(() => {
    if (!createdAt) return false;
    // Only flag teacher/admin uploads — student posts already get plenty of
    // engagement signals (likes, replies). Default to teacher when role is
    // unknown so legacy material docs without `author_role` still get the
    // visual nudge when they're new.
    if (authorRole && !['admin', 'teacher'].includes(authorRole)) return false;
    const t = new Date(createdAt).getTime();
    if (!Number.isFinite(t)) return false;
    return (Date.now() - t) < WINDOW_MS;
  }, [createdAt, authorRole]);

  if (!isFresh) return null;

  const label = lang === 'es' ? 'Nuevo' : 'Neu';

  return (
    <span
      className={`relative inline-flex items-center gap-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full shadow-sm new-badge-anim ${className}`}
      data-testid="new-badge"
    >
      <span className="absolute inset-0 rounded-full bg-amber-400/60 animate-ping" aria-hidden />
      <Sparkle size={9} weight="fill" className="relative" />
      <span className="relative">{label}</span>
    </span>
  );
}
